import React, { useState, useEffect, useContext } from 'react';
import { Input, Button, Checkbox, Avatar, Dialog } from 'components/ui';
import { LuTrash, LuPencil, LuCopy } from 'react-icons/lu';
import { BsCheckCircle } from 'react-icons/bs';
import { HiOutlineUser } from 'react-icons/hi';
import { CompanyPromptsContext } from 'contexts/CompanyPromptsContext';
import { Trans } from 'react-i18next';
import { useWhitelabel } from 'contexts/WhitelabelThemeContext';

const PromptItem = ({ 
    prompt, 
    selectable = false,
    selected = false,
    onSelect = () => void 0,
    refresh = async () => void 0,
    isEditingByDefault = false,
    onAdd = () => void 0,
    className = ""
}) => {
    const { whitelabel } = useWhitelabel();
    const [localPrompt, setLocalPrompt] = useState(prompt);
    const [hasChanges, setHasChanges] = useState(false);
    const [isEditing, setIsEditing] = useState(isEditingByDefault);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const { updatePrompt, deletePrompt } = useContext(CompanyPromptsContext);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isDuplicating, setIsDuplicating] = useState(false);
    const maxLength = 260; // Adjust this value to change when truncation occurs

    const isEditable = !!prompt.company_id;

    useEffect(() => {
        setLocalPrompt(prompt);
    }, [prompt]);

    const handleChange = (changes) => {
        setLocalPrompt(prev => ({
            ...prev,
            ...changes
        }));
        setHasChanges(true);
    };

    const handleSave = async (newPrompt = localPrompt, refresh = true) => {
        setIsSaving(true);
        await updatePrompt(prompt.id, newPrompt, refresh);
        setHasChanges(false);
        setIsEditing(false);
        setIsSaving(false);
    };

    const handleCancel = async () => {
        if (isEditingByDefault && !hasChanges) {
            await handleConfirmDelete();
            await refresh();
            setIsEditing(false);
            setHasChanges(false);
        } else {
            setLocalPrompt(prompt);
            setHasChanges(false);
            setIsEditing(false);
            if (isEditingByDefault) { 
                await refresh();
            }
        }
    };

    const onDelete = () => {
        setShowDeleteDialog(true);
    };

    const handleConfirmDelete = async () => {
        setIsDeleting(true);
        try {
            await deletePrompt(prompt);
        } catch (error) {
            console.error('Error deleting prompt:', error);
        } finally {
            await refresh();
            setShowDeleteDialog(false);
            setIsDeleting(false);
        }
    };

    const truncateText = (text) => {
        if (text.length <= maxLength) return text;
        return isExpanded ? text : `${text.slice(0, maxLength)}...`;
    };

    const handleDuplicate = async () => {
        setIsDuplicating(true);
        await onAdd(localPrompt.name, localPrompt.prompt);
        setIsDuplicating(false);
    };

    const ToggleButton = ({ text }) => (
        <button
            onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
            }}
            className="text-primary-600 hover:text-primary-700 text-xs font-medium mt-1 ml-1"
        >
            {text}
        </button>
    );

    if (selectable) {
        return (
            <div 
                onClick={() => onSelect(prompt)}
                className={`
                    p-5 rounded-2xl border transition-all
                    ${selected ? 'border-primary-600 ring-1 ring-primary-600 !bg-primary-600/10 dark:!bg-primary-900/20' : 'border-tertiary-100 dark:border-gray-600'}
                    ${selectable ? 'cursor-pointer hover:bg-primary-600/10 hover:dark:bg-primary-900/20' : ''}
                    bg-secondary-500 dark:bg-gray-900
                    relative
                    ${className}
                `}
            >
                {selected && (
                    <div className="absolute -top-[1px] -right-[1px] rounded-2xl rounded-tl-none rounded-br-none p-2 bg-primary-600">
                        <BsCheckCircle className="text-white" />
                    </div>
                )}
                <div className="flex flex-col h-full">
                    <div className="flex items-center gap-2 mb-2">
                        {prompt.company_id ? (
                            <Avatar 
                                size={26}
                                src={prompt.user?.image?.url} 
                                icon={<HiOutlineUser />}
                                shape="circle"
                            />
                        ) : (
                            <img 
                                src={whitelabel.logo} 
                                alt={whitelabel?.name} 
                                className="w-6 h-6"
                            />
                        )}
                        <h6>{prompt.name}</h6>
                    </div>
                    <p className="w-full min-h-[100px] py-2 text-xs leading-5">
                        {truncateText(prompt.prompt)}
                        {prompt.prompt.length > maxLength && (
                            <ToggleButton text={isExpanded ? 'View less' : 'View more'} />
                        )}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div 
                className={`
                    p-5 rounded-2xl transition-all border
                    border-tertiary-100 dark:border-gray-600
                    bg-secondary-500 dark:bg-gray-900
                    ${className}
                `}
            >
                <div className="flex flex-col h-full">
                    <div className="flex items-center gap-2 mb-2">
                        {prompt.company_id ? (
                            <Avatar 
                                size={26}
                                src={prompt.user?.image?.url} 
                                icon={<HiOutlineUser />}
                                shape="circle"
                            />
                        ) : (
                            <img 
                                src={whitelabel.logo} 
                                alt={whitelabel?.name} 
                                className="w-6 h-6"
                            />
                        )}
                        {isEditing ? (
                            <Input 
                                className="flex-1 text-tertiary-500 dark:text-gray-400"
                                value={localPrompt.name}
                                onChange={(e) => handleChange({ name: e.target.value })}
                            />
                        ) : (
                            <h6 className="flex-1 text-tertiary-500 dark:text-gray-400">
                                {prompt.name}
                            </h6>
                        )}
                    </div>
                    <div className="flex-grow">
                        {isEditing ? (
                            <textarea
                                className="w-full min-h-[100px] p-2 bg-transparent border rounded-lg focus:ring-1 resize-none"
                                value={localPrompt.prompt}
                                onChange={(e) => handleChange({ prompt: e.target.value })}
                                placeholder="Write your prompt here..."
                            />
                        ) : (
                            <p className="w-full min-h-[100px] py-2 text-xs leading-5">
                                {truncateText(prompt.prompt)}
                                {prompt.prompt.length > maxLength && (
                                    <ToggleButton text={isExpanded ? 'View less' : 'View more'} />
                                )}
                            </p>
                        )}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-4">
                            <Checkbox 
                                checked={localPrompt.default}
                                onChange={(e) => {
                                    handleSave({...localPrompt, default: !localPrompt.default}, false);
                                    handleChange({ default: !localPrompt.default });
                                }}
                                className="text-xs"
                            >
                                <span className="inline-block pt-0.5">Default</span>
                            </Checkbox>
                        </div>
                        <div className="flex gap-0">
                            {isEditing ? (
                                <>
                                    <Button
                                        variant="plain"
                                        size="sm"
                                        onClick={handleCancel}
                                        loading={prompt.isNew && isDeleting}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="plain"
                                        size="sm"
                                        icon={<BsCheckCircle className="text-green-600" />}
                                        onClick={() => handleSave()}
                                        loading={isSaving}
                                    />
                                </>
                            ) : (<>
                                {isEditable && (
                                    <>
                                        <Button
                                            variant="plain"
                                            size="sm"
                                            icon={<LuPencil />}
                                            onClick={() => setIsEditing(true)}
                                        />
                                        <Button
                                            variant="plain"
                                            size="sm"
                                            icon={<LuTrash className="text-red-600" />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDelete();
                                            }}
                                        />
                                    </>
                                )}
                                <Button
                                    variant="plain"
                                    size="sm"
                                    icon={<LuCopy className="text-primary-600" />}
                                    onClick={handleDuplicate}
                                    loading={isDuplicating}
                                />
                            </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <Dialog
                isOpen={showDeleteDialog}
                onClose={() => setShowDeleteDialog(false)}
                onRequestClose={() => setShowDeleteDialog(false)}
            >
                <h5 className="mb-4">
                    <Trans i18nKey="prompts.deletePromptTitle">Delete Prompt</Trans>
                </h5>
                <p className="mb-6">
                    <Trans i18nKey="prompts.deletePromptConfirmation">
                        Are you sure you want to delete the prompt "{prompt.name}"? This action cannot be undone.
                    </Trans>
                </p>
                <div className="flex gap-2 justify-end">
                    <Button
                        variant="plain"
                        onClick={() => setShowDeleteDialog(false)}
                    >
                        <Trans i18nKey="common.cancel">Cancel</Trans>
                    </Button>
                    <Button
                        variant="solid"
                        color="red-600"
                        onClick={handleConfirmDelete}
                        loading={isDeleting}
                    >
                        <Trans i18nKey="common.delete">Delete</Trans>
                    </Button>
                </div>
            </Dialog>
        </>
    );
};

export default PromptItem;